import jsPDF from "jspdf";
import JsBarcode from "jsbarcode";
import { ensureArabicFont, pdfText, hasArabic } from "@/lib/pdfFonts";

const L = {
  es: { title: "MANIFEST DEL MASTER", master: "MASTER", origin: "Origen", dest: "Destino", mode: "Transporte", carrier: "Transportista",
    ref: "Vuelo / Barco / Camión", etd: "ETD", eta: "ETA", date: "Fecha", line: "#", internal: "Tracking interno", ctrack: "Tracking transportista",
    sender: "Remitente", receiver: "Receptor", pkgs: "Paq", units: "Und", weight: "Peso", dims: "L x A x H", cbm: "CBM",
    totals: "TOTALES", tpkgs: "Total paquetes", tunits: "Total unidades", tweight: "Peso total", tcbm: "CBM total" },
  en: { title: "MASTER MANIFEST", master: "MASTER", origin: "Origin", dest: "Destination", mode: "Transport", carrier: "Carrier",
    ref: "Flight / Vessel / Truck", etd: "ETD", eta: "ETA", date: "Date", line: "#", internal: "Internal tracking", ctrack: "Carrier tracking",
    sender: "Sender", receiver: "Receiver", pkgs: "Pkgs", units: "Units", weight: "Weight", dims: "L x W x H", cbm: "CBM",
    totals: "TOTALS", tpkgs: "Total packages", tunits: "Total units", tweight: "Total weight", tcbm: "Total CBM" },
  ar: { title: "بيان الماستر", master: "ماستر", origin: "المنشأ", dest: "الوجهة", mode: "النقل", carrier: "الناقل",
    ref: "رحلة / سفينة / شاحنة", etd: "المغادرة", eta: "الوصول", date: "التاريخ", line: "#", internal: "التتبع الداخلي", ctrack: "تتبع الناقل",
    sender: "المرسل", receiver: "المستلم", pkgs: "شحنات", units: "وحدات", weight: "الوزن", dims: "ط × ع × ا", cbm: "الحجم",
    totals: "الإجماليات", tpkgs: "إجمالي الشحنات", tunits: "إجمالي الوحدات", tweight: "الوزن الإجمالي", tcbm: "الحجم الإجمالي" },
};

const MODE = { aereo: "AEREO / AIR", terrestre: "TERRESTRE / ROAD", maritimo: "MARITIMO / SEA", express: "EXPRESS" };
const TONE = { aereo: [3, 105, 161], terrestre: [180, 83, 9], maritimo: [14, 116, 144], express: [185, 28, 28] };

export async function masterManifestPDF({ master, rows, totales }, lang = "es") {
  const s = L[lang] || L.es;
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  const anyArabic = [s.title, ...rows.flatMap(r => [r.remitente, r.receptor, r.destino, r.origen])].some(hasArabic);
  if (anyArabic) await ensureArabicFont(doc);

  let barcode = null;
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, master.codigo, { format: "CODE128", displayValue: false, height: 40, margin: 0, width: 1.6 });
    barcode = canvas.toDataURL("image/png");
  } catch (e) { console.warn("Master barcode failed:", e); }

  // Header
  doc.setFillColor(15, 23, 42); doc.rect(0, 0, w, 54, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(19); doc.setTextColor(255, 255, 255);
  doc.text("MEOS CARGO", 24, 34);
  doc.setFontSize(10); doc.setTextColor(203, 213, 225);
  pdfText(doc, s.title, 150, 34);
  if (barcode) { try { doc.addImage(barcode, "PNG", w - 190, 10, 130, 26); } catch (e) { console.warn("barcode embed", e); } }
  doc.setFont("courier", "bold"); doc.setFontSize(12); doc.setTextColor(255, 255, 255);
  doc.text(master.codigo, w - 24, 46, { align: "right" });

  // Mode band
  const tone = TONE[master.modo] || TONE.express;
  doc.setFillColor(...tone); doc.rect(0, 54, w, 22, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(255, 255, 255);
  doc.text(MODE[master.modo] || "", 24, 69);
  doc.setFontSize(9);
  doc.text(`${s.date}: ${master.fecha || ""}`, w - 24, 69, { align: "right" });

  // Meta grid
  let y = 90;
  doc.setFillColor(248, 250, 252); doc.rect(20, y, w - 40, 40, "F");
  const meta = [
    [s.origin, master.origen], [s.dest, master.destino], [s.carrier, master.transportista],
    [s.ref, master.transporte_ref], [s.etd, master.etd], [s.eta, master.eta],
  ];
  const cw = (w - 40) / meta.length;
  meta.forEach(([k, v], i) => {
    const x = 28 + i * cw;
    doc.setFont("helvetica", "bold"); doc.setFontSize(6.5); doc.setTextColor(100, 116, 139);
    pdfText(doc, String(k).toUpperCase(), x, y + 14);
    doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(15, 23, 42);
    pdfText(doc, String(v || "—"), x, y + 30);
  });
  y += 52;

  // Table
  const cols = [
    [s.line, 22], [s.internal, 108], [s.ctrack, 88], [s.carrier, 52], [s.sender, 84], [s.receiver, 84],
    [s.origin, 60], [s.dest, 66], [s.pkgs, 26], [s.units, 26], [s.weight, 42], [s.dims, 68], [s.cbm, 38],
  ];
  const total = cols.reduce((a, c) => a + c[1], 0);
  const scale = (w - 40) / total;
  const xs = [];
  let acc = 20;
  cols.forEach(([, cwd]) => { xs.push(acc); acc += cwd * scale; });

  const drawHead = () => {
    doc.setFillColor(15, 23, 42); doc.rect(20, y, w - 40, 18, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(255, 255, 255);
    cols.forEach(([label], i) => pdfText(doc, String(label).toUpperCase(), xs[i] + 4, y + 12));
    y += 18;
  };
  drawHead();

  doc.setFont("helvetica", "normal");
  rows.forEach((r, i) => {
    if (y > h - 70) { doc.addPage(); y = 30; drawHead(); }
    if (i % 2 === 1) { doc.setFillColor(248, 250, 252); doc.rect(20, y, w - 40, 15, "F"); }
    const vals = [
      r.linea, r.tracking_interno, r.tracking_transportista || "—", r.transportista || "—",
      r.remitente || "—", r.receptor || "—", r.origen || "—", r.destino || "—",
      r.paquetes, r.unidades, `${r.peso} kg`, r.largo ? `${r.largo}x${r.ancho}x${r.alto}` : "—", r.cbm,
    ];
    doc.setFontSize(7.2); doc.setTextColor(30, 41, 59);
    vals.forEach((v, ci) => {
      const txt = String(v ?? "");
      const maxw = (cols[ci][1] * scale) - 6;
      if (ci === 1 || ci === 2) { doc.setFont("courier", "bold"); } else { doc.setFont("helvetica", "normal"); }
      pdfText(doc, doc.splitTextToSize(txt, maxw)[0] || "", xs[ci] + 4, y + 10);
    });
    y += 15;
  });

  // Totals
  y += 4;
  doc.setDrawColor(15, 23, 42); doc.setLineWidth(1); doc.line(20, y, w - 20, y);
  y += 6;
  doc.setFillColor(15, 23, 42); doc.rect(20, y, w - 40, 26, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(255, 255, 255);
  pdfText(doc, s.totals, 28, y + 17);
  const tt = [
    [s.tpkgs, totales.paquetes], [s.tunits, totales.unidades],
    [s.tweight, `${totales.peso} kg`], [s.tcbm, totales.cbm],
  ];
  tt.forEach(([k, v], i) => {
    const x = w / 2 - 60 + i * 130;
    doc.setFontSize(6.5); doc.setTextColor(148, 163, 184);
    pdfText(doc, String(k).toUpperCase(), x, y + 10);
    doc.setFontSize(11); doc.setTextColor(255, 255, 255);
    doc.text(String(v), x, y + 22);
  });

  doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(148, 163, 184);
  doc.text(`meoscargo.com · ${master.codigo} · ${new Date().toLocaleString()}`, w / 2, h - 14, { align: "center" });

  doc.save(`Manifest_${master.codigo}.pdf`);
}
